/**
 * A question with listed answers that the person may also answer in their own words.
 *
 * One field, one answer: the value is either one of `field.choices` or whatever was written in the box
 * under them, and `required` is met by either. A short list is drawn as visible options, because the
 * answers the Agent proposed are what the person compares and a closed dropdown hides them; a long
 * list falls back to a dropdown so the form does not grow past the screen.
 */

import { useEffect, useRef, useState } from "react";
import { Icon } from "../components/Icon";
import { RadioGroup, SearchSelect } from "../components/rumonai";
import type { PanelField } from "./spec";

/** At most this many answers are drawn as visible options. */
export const VISIBLE_OPTIONS = 6;

/** What the box is called for a screen reader; on the page it is the one dashed line 「自己写…」. */
export const OWN_WORDS = "或者自己写";
const OWN_PLACEHOLDER = "自己写…";

export function ChoiceOrOwnWords({
  field,
  id,
  value,
  onChange,
  describedBy,
  disabled,
}: {
  field: PanelField;
  id: string;
  value: unknown;
  onChange: (value: unknown) => void;
  describedBy?: string;
  disabled?: boolean;
}) {
  const choices = field.choices ?? [];
  const current = String(value ?? "");
  const listed = choices.includes(current);
  const [own, setOwn] = useState(listed ? "" : current);
  // What this control last said, so a value that moved from outside (a restored draft, a reset) can be
  // told from the person's own typing coming back through `value`.
  const said = useRef(current);
  const say = (next: string) => {
    said.current = next;
    onChange(next);
  };
  useEffect(() => {
    if (current === said.current) return;
    said.current = current;
    setOwn(choices.includes(current) ? "" : current);
  }, [current]);
  const label = (choice: string) => field.choice_labels?.[choice] ?? choice;
  const pick = (choice: string) => {
    setOwn("");
    say(choice);
  };
  // One dashed line until it is pressed: the same input, which fills the line and takes the ring when it has focus.
  const write = (
    <span className="own-words-own pa-field-shell">
      <Icon name="edit" size={16} />
      <input
        id={choices.length <= VISIBLE_OPTIONS ? id : `${id}-own`}
        type="text"
        disabled={disabled}
        className="own-words-write"
        placeholder={OWN_PLACEHOLDER}
        aria-label={`${field.label}：${OWN_WORDS}`}
        aria-describedby={describedBy}
        value={own}
        onChange={(event) => {
          setOwn(event.target.value);
          say(event.target.value);
        }}
      />
      <span className="pa-paint-edge" aria-hidden="true" />
    </span>
  );
  if (choices.length > VISIBLE_OPTIONS) {
    return (
      <div className="own-words" data-testid={`own-words-${field.key}`}>
        <SearchSelect
          id={id}
          name={id}
          label={field.label}
          disabled={disabled}
          value={listed ? current : ""}
          aria-describedby={describedBy}
          onValueChange={pick}
          items={choices.map(choice => ({ value: choice, label: label(choice) }))}
        />
        {write}
      </div>
    );
  }
  return (
    <div className="own-words" data-testid={`own-words-${field.key}`}>
      <RadioGroup label={field.label} name={id} value={listed ? current : ""} onValueChange={pick} disabled={disabled}
        className="own-words-options" items={choices.map(choice => ({ value: choice, label: label(choice) }))} />
      {write}
    </div>
  );
}
